import type { ReactNode } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardTitleCount,
  CardHeaderActions,
  CardContent,
} from '@/components/Card'
import { type JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'
import styles from './JobScopeChangeRequestsCard.module.css'
import { Avatar } from '@/components/Avatar'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'

import { useToggle } from '@/hooks/state-hooks'
import { Button } from '@/components/Button'
import logoWorklayerWhiteUrl from '@/assets/misc/logo_worklayer-white.svg'
import { dateFormatters } from '@/utils/date-util'

export interface JobScopeChangeRequestsCardProps {
  count: number
  showActions: boolean
  showUpdateJobScopeOption: boolean
  onUpdateJobScope: () => unknown
  onRequestScopeChanges: () => unknown
  children: ReactNode
}

export function JobScopeChangeRequestsCard({
  count,
  showActions,
  showUpdateJobScopeOption,
  onUpdateJobScope,
  onRequestScopeChanges,
  children,
}: JobScopeChangeRequestsCardProps) {
  return (
    <Card testId="job-scope-change-requests-card">
      <CardHeader>
        <CardTitle>
          Job scope updates <CardTitleCount>({count})</CardTitleCount>
        </CardTitle>

        <CardHeaderActions>
          {showActions && (
            <ScopeChangeRequestsActionsDropdown
              showUpdateJobScopeOption={showUpdateJobScopeOption}
              onUpdateJobScope={onUpdateJobScope}
              onRequestScopeChanges={onRequestScopeChanges}
            />
          )}
        </CardHeaderActions>
      </CardHeader>

      <CardContent scroll compact>
        {children}
      </CardContent>
    </Card>
  )
}

export interface ScopeChangeRequestProps {
  status: JobScopeChangeStatus
  isPending: boolean
  isAccepted: boolean
  isRejected: boolean
  isApplyFailed: boolean
  isCreatedByAdmin: boolean
  createTime: Date
  latestResponseTime?: Date
  onView: () => unknown
}

export function ScopeChangeRequest({
  status,
  isPending,
  isAccepted,
  isRejected,
  isApplyFailed,
  isCreatedByAdmin,
  createTime,
  latestResponseTime,
  onView,
}: ScopeChangeRequestProps) {
  return (
    <div
      className={styles.root}
      data-test={`job-scope-change-request-${status}`}
    >
      <div className={styles.container}>
        <div className={styles.avatarContainer}>
          <Avatar
            size={32}
            backgroundColor={
              isPending ? 'var(--brand-primary)' : 'var(--grey500)'
            }
            badge={
              isAccepted
                ? 'check-mark'
                : isRejected || isApplyFailed
                  ? 'error-mark'
                  : 'none'
            }
            subject={{
              avatar: logoWorklayerWhiteUrl,
            }}
          />
        </div>

        <div className={styles.content}>
          <p className={styles.description}>
            <strong>
              {isCreatedByAdmin && (isPending || isRejected || isApplyFailed)
                ? 'An admin has identified services that fall outside the original job scope.'
                : isCreatedByAdmin && isAccepted
                  ? 'An admin has updated the job scope.'
                  : 'You have identified services that fall outside the original job scope.'}
            </strong>
          </p>

          <div className={styles.date}>
            {isAccepted && (
              <>
                Approved on{' '}
                {dateFormatters.dateTimeDefault.format(
                  latestResponseTime ?? createTime,
                )}
              </>
            )}

            {isRejected && (
              <>
                Declined on{' '}
                {dateFormatters.dateTimeDefault.format(latestResponseTime)}
              </>
            )}

            {isApplyFailed && (
              <>
                System rejected on{' '}
                {dateFormatters.dateTimeDefault.format(latestResponseTime)}
              </>
            )}

            {isPending && (
              <strong className={styles.pending}>Pending review</strong>
            )}
          </div>
        </div>

        <Button label="View" onPress={onView} />
      </div>
    </div>
  )
}

interface ScopeChangeRequestsActionsDropdownProps {
  showUpdateJobScopeOption: boolean
  onUpdateJobScope: () => unknown
  onRequestScopeChanges: () => unknown
}

function ScopeChangeRequestsActionsDropdown({
  showUpdateJobScopeOption,
  onUpdateJobScope,
  onRequestScopeChanges,
}: ScopeChangeRequestsActionsDropdownProps) {
  const dropdownOpened = useToggle(false)

  return (
    <Dropdown
      width="medium"
      isOpen={dropdownOpened.isEnabled}
      onRequestClose={dropdownOpened.disable}
      onButtonPress={dropdownOpened.toggle}
      position="bottom-end"
      button={<IconButton name="more" label="Scope change request actions" />}
      testId="job-scope-change-requests-card-dropdown"
    >
      <DropdownTitle>Actions</DropdownTitle>
      {showUpdateJobScopeOption ? (
        <DropdownItem
          relative
          onPress={onUpdateJobScope}
          testId="update-job-scope-card-dropdown-item"
        >
          <DropdownItemTitle>Update job scope</DropdownItemTitle>
        </DropdownItem>
      ) : (
        <DropdownItem
          relative
          onPress={onRequestScopeChanges}
          testId="request-scope-change-card-dropdown-item"
        >
          <DropdownItemTitle>Request scope changes</DropdownItemTitle>
        </DropdownItem>
      )}
    </Dropdown>
  )
}
